import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader, SectionCard } from '@/components/admin/page';
import { Pager } from '../../lib/Pager';
import { api, ApiError } from '../../lib/api';
import { displayDeviceName, displayIdentifier } from '../../lib/format';
import { useI18n } from '../../lib/i18n';

interface AgentRow {
  id: string;
  tenantId: string;
  userId: string;
  deviceName: string | null;
  apiKeyId: string | null;
  lastSeenSec: number;
}

interface AgentsResponse {
  connectionCount: number;
  tenantCount: number;
  connections: AgentRow[];
}

const PAGE_SIZE = 20;

export function AgentsPage() {
  const { t } = useI18n();
  const [data, setData] = useState<AgentsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const load = () => {
      api<AgentsResponse>('/v1/admin/agents')
        .then((next) => { setData(next); setError(null); })
        .catch((e) => setError(e instanceof ApiError ? e.message : (e as Error).message));
    };
    load();
    const id = setInterval(load, 10_000);
    return () => clearInterval(id);
  }, []);

  if (error) return <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>;
  if (!data) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  const total = data.connections.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const slice = data.connections.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div>
      <PageHeader
        title={t('agents.title')}
        description={t('agents.desc', { n: data.connectionCount, t: data.tenantCount })}
      />
      <SectionCard title={t('agents.section')} description={t('agents.sectionDesc')}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.device')}</TableHead>
              <TableHead>{t('common.userId')}</TableHead>
              <TableHead>{t('common.workspaceId')}</TableHead>
              <TableHead>{t('agents.col.apiKey')}</TableHead>
              <TableHead className="text-right">{t('common.lastActive')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {slice.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  <div className="flex items-center gap-2 font-medium">
                    <span className={a.lastSeenSec < 60 ? 'size-2 rounded-full bg-emerald-400' : 'size-2 rounded-full bg-amber-400'} />
                    {displayDeviceName(a.deviceName) ?? t('common.unnamed')}
                  </div>
                </TableCell>
                <TableCell>{displayIdentifier(a.userId, t('common.userId'))}</TableCell>
                <TableCell>{displayIdentifier(a.tenantId, t('common.workspaceId'))}</TableCell>
                <TableCell>{displayIdentifier(a.apiKeyId, t('agents.col.apiKey')) ?? '–'}</TableCell>
                <TableCell className="text-right"><Badge variant={a.lastSeenSec < 60 ? 'success' : 'warning'}>{t('agents.lastSec', { n: a.lastSeenSec })}</Badge></TableCell>
              </TableRow>
            ))}
            {slice.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">{t('agents.empty')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <Pager page={safePage} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
      </SectionCard>
    </div>
  );
}
